/**
 * The holiday-treat GIFT ROW — "Free pumpkin cookie · included with lunch".
 *
 * A treat is never ordered: it rides every meal the family orders at the
 * mealtime on the treat date (the kitchen packs one per meal). So the row has
 * two states: a teaser while the child has no meal in the cart ("Add a lunch
 * and get a free …") and the Sephora moment once they do ("Gift added · ×2").
 * No stepper, no price — the badge says Free. Tapping opens the item detail
 * (allergens, ingredients), which the server allows for treat items even
 * though their menu is on the INTERNAL line.
 */

import { Badge } from '@ordo/ui/badge'
import { cn } from '@ordo/ui/cn'
import { Gift } from 'lucide-react'
import type { ReactElement } from 'react'

import type { HolidayTreatWire } from '@/api/orders'
import { AssetImage } from '@/components/asset-image'

export function holidayTreatSubtitle(input: {
  treat: Pick<HolidayTreatWire, 'name' | 'perMeal'>
  mealCount: number
  mealtimeLabel: string
  receipt: boolean
}): string {
  const meal = input.mealtimeLabel.toLowerCase()
  if (input.mealCount <= 0) {
    return input.receipt
      ? `Included with every ${meal}`
      : `Add a ${meal} and get a free ${input.treat.name.toLowerCase()}`
  }
  const count = input.mealCount * Math.max(1, input.treat.perMeal)
  return `${input.receipt ? 'Included' : 'Gift added'} · ×${count} · free with ${meal}`
}

export function HolidayTreatGiftRow({
  treat,
  mealCount,
  mealtimeLabel,
  receipt,
  onOpenDetail,
}: {
  treat: HolidayTreatWire
  /** Meals the child has (or ordered) at this mealtime — drives the two states. */
  mealCount: number
  mealtimeLabel: string
  receipt: boolean
  onOpenDetail: (menuItemId: string) => void
}): ReactElement {
  const active = mealCount > 0
  return (
    <li className={cn('flex items-center gap-3 px-4 py-3', !active && 'opacity-80')}>
      <button
        type="button"
        onClick={() => onOpenDetail(treat.menuItemId)}
        title="View treat details"
        className="group flex min-w-0 flex-1 items-center gap-3 text-left focus:outline-none focus-visible:outline-none">
        <span className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-emerald-50 dark:bg-emerald-950/40">
          <AssetImage
            src={treat.imageUri}
            className="h-full w-full object-cover"
            fallback={<Gift className="h-5 w-5 text-emerald-700 dark:text-emerald-300" />}
          />
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-base font-medium text-foreground group-hover:underline">
              Free {treat.name}
            </span>
            <Badge
              variant="secondary"
              className="shrink-0 gap-1 bg-emerald-50 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
              <Gift className="h-3 w-3" strokeWidth={1.75} />
              {active ? 'Gift added' : 'Free gift'}
            </Badge>
          </span>
          <span className="text-sm text-muted-foreground">
            {holidayTreatSubtitle({ treat, mealCount, mealtimeLabel, receipt })}
          </span>
          {treat.allergens.length > 0 ? (
            <span className="text-xs text-muted-foreground">
              Contains {treat.allergens.map((a) => a.name).join(', ')}
            </span>
          ) : null}
        </span>
      </button>
      <span className="shrink-0 text-sm font-semibold text-emerald-700 dark:text-emerald-300">Free</span>
    </li>
  )
}
